import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
} from '@/components/shared/UISectionWrapper';
import UISelect from '@/components/shared/UISelect';
import UISimpleTable, {
  SimpleTableColumn,
} from '@/components/shared/UISimpleTable';
import UITenantInfo from '@/components/shared/UITenantInfo';
import UIBadge from '@/components/shared/UIBadge';
import { useTickets } from '@/hooks/useTickets';
import {
  Ticket,
  TicketDictionariesResponse,
} from '@/types/tickets';
import useGetDictionaries from '@/hooks/useGetDictionaries';
import './TicketsTransactionsSection.scss';
import UIPaginator from '@/components/shared/UIPaginator';
import UIIconButton from '@/components/shared/UIIconButton';
import TicketDetailsModal from '@/components/dashboard/Tickets/TicketDetailsModal/TicketDetailsModal';
import { safeToLowerCase } from '@/utils/stringUtils';

interface TicketsTransactionsSectionProps {
  selectedProperty: string;
  selectedTenant: string;
  selectedStatus: string;
  selectedCategory: string;
  onPropertyChange: (property: string) => void;
  onTenantChange: (tenant: string) => void;
  onStatusChange: (status: string) => void;
  onCategoryChange: (category: string) => void;
}

const TicketsTransactionsSection: React.FC<
  TicketsTransactionsSectionProps
> = ({
  selectedProperty,
  selectedTenant,
  selectedStatus,
  selectedCategory,
  onPropertyChange,
  onTenantChange,
  onStatusChange,
  onCategoryChange,
}) => {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsData, setTicketsData] = useState<Ticket[]>([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const navigate = useNavigate(); // Initialize useNavigate

  const itemsPerPage = 5;
  const { getTickets, loading, error } = useTickets();
  const {
    data: dictionaries,
    isLoading: isDictionariesLoading,
    error: dictionariesError,
  } = useGetDictionaries<TicketDictionariesResponse>('tickets');

  const propertyOptions = useMemo(() => {
    if (!dictionaries?.properties)
      return [{ value: 'all-properties', label: 'All properties' }];
    return [
      { value: 'all-properties', label: 'All properties' },
      ...dictionaries.properties.map((property) => ({
        value: property.id.toString(),
        label: property.name,
      })),
    ];
  }, [dictionaries?.properties]);

  const tenantOptions = useMemo(() => {
    if (!dictionaries?.tenants)
      return [{ value: 'all-tenants', label: 'All tenants' }];
    return [
      { value: 'all-tenants', label: 'All tenants' },
      ...dictionaries.tenants.map((tenant) => ({
        value: tenant.id.toString(),
        label: tenant.name,
      })),
    ];
  }, [dictionaries?.tenants]);

  const statusOptions = useMemo(() => {
    if (!dictionaries?.ticketStatuses)
      return [{ value: 'all-statuses', label: 'All statuses' }];
    return [
      { value: 'all-statuses', label: 'All statuses' },
      ...dictionaries.ticketStatuses.map((status) => ({
        value: status.id,
        label: status.name,
      })),
    ];
  }, [dictionaries?.ticketStatuses]);

  const categoryOptions = useMemo(() => {
    if (!dictionaries?.ticketCategories)
      return [{ value: 'all-categories', label: 'All categories' }];
    return [
      { value: 'all-categories', label: 'All categories' },
      ...dictionaries.ticketCategories.map((category) => ({
        value: category,
        label: category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      })),
    ];
  }, [dictionaries?.ticketCategories]);

  const loadTickets = useCallback(async () => {
    setIsDataLoading(true);
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const filters = {
        propertyId: selectedProperty,
        tenantId: selectedTenant,
        status: selectedStatus,
        category: selectedCategory,
        limit: itemsPerPage,
        offset,
      };
      const data = await getTickets(filters);
      if (data) {
        setTicketsData(data.tickets || []);
        setTotalTickets(data.total);
      }
    } catch (err) {
      console.error('Failed to load tickets:', err);
    } finally {
      setIsDataLoading(false);
    }
  }, [
    currentPage,
    getTickets,
    selectedProperty,
    selectedTenant,
    selectedStatus,
    selectedCategory,
  ]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handlePropertyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onPropertyChange(event.target.value);
    setCurrentPage(1);
  };

  const handleTenantChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onTenantChange(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onStatusChange(event.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onCategoryChange(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDetailsClick = (ticketId: string) => {
    setSelectedTicketId(ticketId);
    setIsDetailsModalOpen(true);
  };

  const handleDetailsModalClose = () => {
    setIsDetailsModalOpen(false);
    setSelectedTicketId(null);
  };

  const handleEditClick = (ticketId: string) => {
    navigate(`/dashboard/tickets/edit/${ticketId}`);
  };

  const currentTickets = ticketsData;
  const totalItems = totalTickets;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const pageInfo = `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, totalItems)} of ${totalItems}`;

  const columns: SimpleTableColumn[] = [
    {
      key: 'createdAt',
      label: 'DATE',
      headerContent: <span>DATE</span>,
      render: (value, row: Ticket) => <span>{new Date(row.createdAt).toLocaleDateString()}</span>,
    },
    {
      key: 'id',
      label: 'TICKET ID',
      headerContent: <span>TICKET ID</span>,
      render: (value, row: Ticket) => <span>#{row.id}</span>,
    },
    {
      key: 'title',
      label: 'SUBJECT',
      headerContent: <span>SUBJECT</span>,
      render: (value, row: Ticket) => <span>{row.title}</span>,
    },
    {
      key: 'category',
      label: 'CATEGORY',
      headerContent: <span>CATEGORY</span>,
      render: (value, row: Ticket) => (
        <span>
          {row.category
            ? row.category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
            : '-'}
        </span>
      ),
    },
    {
      key: 'tenant',
      label: 'TENANT',
      headerContent: <span>TENANT</span>,
      render: (value, row: Ticket) => {
        const tenant = row.lease?.tenant;
        if (!tenant) {
          return <span>-</span>;
        }
        return (
          <Link
            to={`/dashboard/tenants/${tenant.id}`}
            className="TicketsTransactionsSection__tenant-link"
          >
            <UITenantInfo
              name={`${tenant.firstname} ${tenant.lastname}`}
              email={tenant.email}
              tenantId={tenant.id}
              avatar={tenant.avatar}
              size="medium"
            />
          </Link>
        );
      },
    },
    {
      key: 'property',
      label: 'PROPERTY',
      headerContent: <span>PROPERTY</span>,
      render: (value, row: Ticket) => {
        const property = row.lease?.unit?.property;
        if (!property) {
          return <span>-</span>;
        }
        return (
          <Link
            to={`/dashboard/properties/${property.id}`}
            className="TicketsTransactionsSection__property-link"
          >
            <div className="TicketsTransactionsSection__property-cell">
              <span className="TicketsTransactionsSection__property-name">
                {property.name}
              </span>
              <UIBadge variant="success" shape="default">
                {(property.type || 'No units').toUpperCase()}
              </UIBadge>
            </div>
          </Link>
        );
      },
    },
    {
      key: 'unit',
      label: 'UNIT',
      headerContent: <span>UNIT</span>,
      render: (value, row: Ticket) => <span>{row.lease?.unit?.number || '-'}</span>,
    },
    {
      key: 'status',
      label: 'STATUS',
      headerContent: <span>STATUS</span>,
      render: (value, row: Ticket) => {
        const getStatusBadge = (status: string) => {
          switch (safeToLowerCase(status)) {
            case 'open':
              return <UIBadge variant="primary" shape="default">OPEN</UIBadge>;
            case 'in_progress':
              return <UIBadge variant="info" shape="default">IN PROGRESS</UIBadge>;
            case 'closed':
              return <UIBadge variant="success" shape="default">CLOSED</UIBadge>;
            case 'on_hold':
              return <UIBadge variant="warning" shape="default">ON HOLD</UIBadge>;
            default:
              return <UIBadge variant="secondary" shape="default">{status?.toUpperCase()}</UIBadge>;
          }
        };
        return getStatusBadge(row.status);
      },
    },
    {
      key: 'actions',
      label: '',
      headerContent: <span />,
      render: (value, row: Ticket) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <UIIconButton
            iconType="info"
            variant="info"
            size="medium"
            onClick={() => handleDetailsClick(row.id.toString())}
          />
          <UIIconButton
            iconType="edit" // Assuming 'edit' iconType exists
            variant="primary" // Or another appropriate variant
            size="medium"
            onClick={() => handleEditClick(row.id.toString())}
          />
          {row.lease?.tenant && (
            <Link to={`/dashboard/chats?chatId=${row.lease.tenant.id}`}>
              <UIIconButton
                iconType="contact"
                variant="success"
                size="medium"
              />
            </Link>
          )}
        </div>
      ),
    },
  ];

  return (
    <UISectionWrapper spacing="default" align="stretch" shadow="default">
      <UISectionHeader>
        <div className="TicketsTransactionsSection__header">
          <div className="TicketsTransactionsSection__title-row">
            <div className="TicketsTransactionsSection__title-content">
              <h2 className="TicketsTransactionsSection__title">
                Tickets for
              </h2>
              <UISelect
                size="medium"
                value={selectedProperty}
                onChange={handlePropertyChange}
                options={propertyOptions}
                className="TicketsTransactionsSection__property-select"
                disabled={isDictionariesLoading || loading || isDataLoading}
              />
              <UISelect
                size="medium"
                value={selectedTenant}
                onChange={handleTenantChange}
                options={tenantOptions}
                className="TicketsTransactionsSection__tenant-select"
                disabled={isDictionariesLoading || loading || isDataLoading}
              />
              <UISelect
                size="medium"
                value={selectedStatus}
                onChange={handleStatusChange}
                options={statusOptions}
                className="TicketsTransactionsSection__status-select"
                disabled={isDictionariesLoading || loading || isDataLoading}
              />
              <UISelect
                size="medium"
                value={selectedCategory}
                onChange={handleCategoryChange}
                options={categoryOptions}
                className="TicketsTransactionsSection__category-select"
                disabled={isDictionariesLoading || loading || isDataLoading}
              />
            </div>
          </div>
          <div className="TicketsTransactionsSection__separator"></div>
        </div>
      </UISectionHeader>

      <UISectionBody>
        <div className="TicketsTransactionsSection__content">
          {(isDictionariesLoading || loading || isDataLoading) && (
            <div className="TicketsTransactionsSection__loading">
              Loading...
            </div>
          )}
          {(error || dictionariesError) && (
            <div className="TicketsTransactionsSection__error">
              Error loading data.
            </div>
          )}
          {!isDictionariesLoading &&
            !loading &&
            !error &&
            !dictionariesError && (
              <>
                <UISimpleTable
                  data={currentTickets}
                  columns={columns}
                  className="TicketsTransactionsSection__table"
                  getRowKey={(row) => row.id}
                />
                {totalPages > 1 && (
                  <div className="TicketsTransactionsSection__pagination">
                    <div className="TicketsTransactionsSection__page-info">
                      {pageInfo}
                    </div>
                    <UIPaginator
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      className="TicketsTransactionsSection__paginator"
                    />
                  </div>
                )}
                {currentTickets.length === 0 && (
                  <div className="TicketsTransactionsSection__no-data">
                    No tickets found.
                  </div>
                )}
              </>
            )}
        </div>
      </UISectionBody>

      {selectedTicketId && (
        <TicketDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={handleDetailsModalClose}
          ticketId={selectedTicketId}
        />
      )}
    </UISectionWrapper>
  );
};

export default TicketsTransactionsSection;
